import React, {
  useState,
  useMemo,
  useLayoutEffect,
  useRef,
  useEffect,
} from 'react';
import { useIntl } from 'context/IntlContext';
import { useProducts } from 'context/SavingProductsContext';
import * as d3 from 'd3';
import { Text, Button, Box } from 'components';
import Link from 'components/Link';
import {
  ChartSVG,
  LegendBlock,
  LegendContainer,
  AccountInfo,
  LegendValue,
  LegendColor,
  MouseDetectionArea,
  AxisText,
  StaticAxisText,
  InitialFundsContainer,
  SlideInput,
  SliderAndLegendContainer,
} from './styled';
import Info from '../icons/info-circle.svg';
import { useLinks } from 'context/LinksContext';

import LoadingIcon from '../../../../../../images/icons/loading.svg';


const AxisLabels = ({ layout, mouseX, xScale }) => {
  const { height, width, margins } = layout;
  const { t } = useIntl();
  const verticalPlacement = height + 25;

  const labelHide = 80;
  let movingLabelAlignment = 'middle';
  if (mouseX < margins.left + labelHide) {
    movingLabelAlignment = 'start';
  } else if (mouseX > width - labelHide) {
    movingLabelAlignment = 'end';
  }

  let movingLabel = '';

  return (
    <g>
      <StaticAxisText
        hide={mouseX < margins.left + labelHide}
        x={margins.left}
        y={verticalPlacement}
        textAnchor="start"
      >
        {t('calculator.today')}
      </StaticAxisText>
      <AxisText
        x={mouseX}
        y={verticalPlacement}
        textAnchor={movingLabelAlignment}
      >
        {movingLabel}
      </AxisText>
    </g>
  );
};

const Legend = ({ product, months, deposit, t }) => {
  if( product.calculatorColor ){
    const sqcolor = product.calculatorColor.css;
    const {
      productName,
      interestRate,
    } = product;
    const { formatMoney } = useIntl();
    return (
      <LegendBlock>
        <LegendColor sqcolor={sqcolor} />
        <AccountInfo>
          <div>{interestRate}</div>
          <div>%</div>
          <div>{t(`${productName}`)}</div>
        </AccountInfo>
        <LegendValue>{formatMoney(product.formula(deposit)(months))}</LegendValue>
      </LegendBlock>
    );
  } else {
    return null;
  }
};

const ProductPath = ({ product, generator, values }) => {
  if( product.calculatorColor ){
    const {
      productName,
      calculatorColor: { css },
    } = product;
    const area = generator(values);
    return <path key={productName} fill={css} d={area} />;
  } else {
    return null;
  }
};

const PathDots = ({ mouseX, rates, mouseMonths, yScale, width }) => {
  const minY = rates.reduce(
    (min = 0, el) => Math.min(min, yScale(el(mouseMonths))),
    1000,
  );
  const maxY = rates.reduce(
    (max = 0, el) => Math.max(max, yScale(el(mouseMonths))),
    0,
  );
  return (
    <g>
      <line x1={mouseX} x2={mouseX} y1={minY} y2={maxY} stroke="#000" />
      {rates.map((el, k) => (
        <circle
          key={`shadow_${k}`}
          cx={mouseX}
          cy={yScale(el(mouseMonths))}
          r={7}
          filter="url(#blurMe)"
          fill="rgba(0,0,0,0.5)"
        />
      ))}
      {rates.map((el, k) => (
        <circle
          key={`dot_${k}`}
          cx={mouseX}
          cy={yScale(el(mouseMonths))}
          r={7}
          stroke="#eee"
          strokeWidth="1"
          fill="#fafafa"
        />
      ))}
    </g>
  );
};

const useChartMousePosition = ({ startPosition, marginOffset, scale }) => {
  const [mouseX, setMouseX] = useState(startPosition);
  const scaleValue = scale.invert(mouseX - marginOffset);
  return [mouseX, scaleValue, setMouseX];
};

const Slider = ({ val, setter }) => {
  const change = (e) => {
    const v = e.target.value;
    setter(v === '0' ? 1 : v);
  };
  return (
    <SlideInput
      type="range"
      min={0}
      step={100}
      max={250000}
      value={val}
      onChange={change}
    />
  );
};

const useDimensions = () => {
  const ref = useRef();
  const [dimensions, setDimensions] = useState({});
  const [resizeTrigger, setResize] = useState(0);
  useLayoutEffect(() => {
    const a = ref.current.getBoundingClientRect();
    setDimensions(a);
  }, [resizeTrigger]);

  useEffect(() => {
    const handleResize = () => {
      setResize((a) => a + 1);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return [ref, dimensions];
};

const calcCompound = (interestRate) => (firstInvestment) => (months) =>
  firstInvestment * (1 + parseFloat(interestRate) / 100) ** (months / 12);

export const CalculatorChart = ({ timeMaxYears, deposit }) => {
  const { productList: savingProductsList } = useProducts();
  const [initialDeposit, setInitialDeposit] = useState(deposit);
  const [products, setProducts] = useState<any>([]);
  const [graphValues, setGraphValues] = useState<any>([]);
  const { t, formatMoneyShort } = useIntl();
  const [ref, dimensions] = useDimensions();
  const { getSignUpLocalLink, isLoadingCountry } = useLinks();

  const fullWidth = dimensions.width || 320;
  const fullHeight = dimensions.height || 300;

  const margins = {
    top: 0,
    right: 0,
    left: 0,
    bottom: 40,
  };

  const width = fullWidth - margins.left - margins.right;
  const height = fullHeight - margins.top - margins.bottom;

  const layout = {
    width,
    height,
    margins,
  };

  useEffect(() => {
    let isMounted = true;

    if (!isMounted || !savingProductsList || savingProductsList?.length === 0)
      return;

    const products = savingProductsList
      .filter((p) => p.compoundInterest)
      .map((p) => ({ formula: calcCompound(p.interestRate), ...p }));

    setProducts(products);

    const graphValues = products.map((product) => {
      const interest = product.formula(1);
      const data = Array.from(
        { length: timeMaxYears * 12 + 1 },
        (v, i) => i,
      ).map((v) => ({
        month: v,
        value: interest(v),
      }));
      return [product, data];
    });

    setGraphValues(graphValues);

    return () => {
      isMounted = false;
    };
  }, [timeMaxYears, savingProductsList]);

  const timeScale = useMemo(() => {
    if (!timeMaxYears || !width) return;
    return d3
      .scaleLinear()
      .domain([0, timeMaxYears * 12])
      .range([0, width]);
  }, [timeMaxYears, width]);

  const moneyScale = useMemo(() => {
    if (!height || !graphValues || graphValues.length === 0) return;
    return d3
      .scaleLinear()
      .domain([
        1,
        d3.max(
          graphValues.length >= 3 ? graphValues[2][1] : 0,
          (d) => +d?.value,
        ) * 1.05,
      ])
      .range([height, 0]);
  }, [height, graphValues]);

  const graphAreaScale = useMemo(() => {
    if (!timeMaxYears || !width) return;
    return d3
      .scalePow()
      .exponent(0.75)
      .domain([0, timeMaxYears * 12])
      .range([0, width]);
  }, [timeMaxYears, width]);

  const areaGraph = useMemo(() => {
    if (!graphAreaScale || !moneyScale) return;
    return d3
      .area()
      .x((d) => graphAreaScale(d.month))
      .y0(moneyScale(1))
      .y1((d) => moneyScale(d.value));
  }, [graphAreaScale, moneyScale]);

  const [mouseX, mouseValue, setMouseX] = useChartMousePosition({
    startPosition: fullWidth / 2,
    marginOffset: margins.left,
    scale: timeScale,
  });

  const mouseMove = ({ nativeEvent }) => {
    setMouseX(nativeEvent.offsetX);
  };
  const touchMove = (e) => {
    e.preventDefault();
    const max = Math.ceil(dimensions.width - margins.right);
    const min = margins.left;
    const v = e.nativeEvent.touches[0].pageX - dimensions.left;
    let trimedV = v < min ? min : v;
    trimedV = trimedV > max ? max : trimedV;
    setMouseX(trimedV);
  };

  return (
    <div style={{ zIndex: 3 }}>
      <Box
        sx={{
          bg: 'secondaryBackground',
          textAlign: ['initial', 'initial', 'initial', 'center'],
        }}
      >
        <Text
          as="p"
          variant="sectionTitle"
          sx={{
            opacity: '0.8',
          }}
        >
          {t('homepage.marketplace.projection_section.title')}
        </Text>
        <Text
          variant="underHero"
          sx={{
            color: 'quaternary',
            opacity: '0.8',
            pt: 5,
            pb: 14,
          }}
        >
          {t('homepage.marketplace.projection_section.subtitle')}
        </Text>

        <SliderAndLegendContainer>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
            }}
          >
            <InitialFundsContainer>
              <div>{t('home.calculator.input.placeholder')}</div>
              <div>{formatMoneyShort(initialDeposit)}</div>
            </InitialFundsContainer>
            <Text
              variant="smallReferences"
              sx={{
                color: 'quaternary',
                opacity: '0.8',
                pt: 5,
                pb: 8,
              }}
            >
              {t('homepage.marketplace.projection_section.initial_funds')}
            </Text>
          </Box>
          <Slider val={initialDeposit} setter={setInitialDeposit} />
          <LegendContainer>
            {products.map((p) => (
              <Legend
                key={p.productName}
                product={p}
                deposit={initialDeposit}
                months={mouseValue}
                t={t}
              />
            ))}
          </LegendContainer>
        </SliderAndLegendContainer>
        <div style={{ zIndex: 10 }}>
          <ChartSVG height={fullHeight} ref={ref}>
            <defs>
              <clipPath id="areaClip">
                <rect width={width} height={height} x="0" y="0" />
              </clipPath>
              <filter id="blurMe" width="200%" height="200%" x="-50%" y="-50%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" />
              </filter>
            </defs>
            <g
              clipPath="url(#areaClip)"
              transform={`translate(${margins.left}, ${margins.top})`}
            >
              {graphValues?.map((d) => (
                <ProductPath
                  key={d[0].productName}
                  product={d[0]}
                  generator={areaGraph}
                  values={d[1]}
                />
              ))}
            </g>
            {timeScale && (
              <AxisLabels layout={layout} mouseX={mouseX} xScale={timeScale} />
            )}
            {moneyScale && (
              <PathDots
                mouseX={mouseX}
                mouseMonths={mouseValue}
                width={fullWidth}
                rates={products.map((p) => p.formula(1))}
                yScale={moneyScale}
              />
            )}
            <MouseDetectionArea
              width={width}
              height={fullHeight}
              x={margins.left}
              y={0}
              onMouseMove={mouseMove}
              onTouchMove={touchMove}
            />
          </ChartSVG>
        </div>
        <Box sx={{ position: 'relative', mb: 14 }}>
          {timeScale && (
            <>
              <Text
                variant="smallReferences"
                sx={{
                  color: '#293040',
                  opacity: '0.5',
                  fontSize: 0,
                  position: 'absolute',
                  left: timeScale(0),
                }}
              >
                {t('homepage.marketplace.projection_section.today')}
              </Text>
              <Text
                variant="smallReferences"
                sx={{
                  color: '#293040',
                  opacity: '0.5',
                  fontSize: 0,
                  position: 'absolute',
                  left: timeScale(12 * 5),
                }}
              >
                {t('homepage.marketplace.projection_section.5_years')}
              </Text>
              <Text
                variant="smallReferences"
                sx={{
                  color: '#293040',
                  opacity: '0.5',
                  fontSize: 0,
                  position: 'absolute',
                  left: timeScale(12 * 10),
                }}
              >
                10 {t('homepage.home.hero.phone.year_copy').toLocaleLowerCase()}
              </Text>
              <Text
                variant="smallReferences"
                sx={{
                  color: '#293040',
                  opacity: '0.5',
                  fontSize: 0,
                  position: 'absolute',
                  left: timeScale(11.5 * 15),
                }}
              >
                {t('homepage.marketplace.projection_section.15_years')}
              </Text>
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: ['initial', 'initial', 'initial', 'center'],
          }}
        >
          <Info />
          <Text
            variant="underHero"
            sx={{
              color: 'quaternary',
              opacity: '0.8',
              pl: 3,
              pb: 9,
              fontSize: [0, 0, 0, 0],
            }}
          >
            {t('homepage.marketplace.projection_section.disclaimer')}
          </Text>
        </Box>
        <Box>
          <Button
            as={Link}
            to={getSignUpLocalLink()}
            variant="secondary"
            sx={{ m: '0 auto' }}
            className={isLoadingCountry ? 'disable-links' : ''}
          >
            {
              isLoadingCountry ? 
                <LoadingIcon className="rotate icon icon-primary" /> : 
                t('global.buttons.start_investing')
            }
          </Button>
        </Box>
        <Text
          sx={{
            color: '#233143',
            fontSize: '12px',
            lineHeight: '24px',
            marginTop: '16px',
            display: 'block',
          }}
        >
          {t('marketplace.provider.cta.warning')}
        </Text>
      </Box>
    </div>
  );
};
